/* eslint-disable no-param-reassign  */

import { QueryParameters, SupportedLocale } from '@tronic-sdk/types';
import { coerce, satisfies } from '../util/semver';
import { encodeJSON } from '../util/base64-json';
import {
  createMissingApiKeyError,
  createIncompatibleExtensionsError,
  createMissingEndpointError,
} from './sdk-exceptions';
import { UserModule } from '../modules/user';
import { WalletModule } from '../modules/wallet';
import { ViewController } from './view-controller';
import { createURL } from '../util/url';
import { Extension } from '../modules/base-extension';
import { isEmpty } from '../util/type-guards';
import { SDKEnvironment, sdkNameToEnvName } from './sdk-environment';

/**
 * Checks if the given `ext` is compatible with the platform & version of Tronic
 * SDK currently in use.
 */
function checkExtensionCompat(ext: Extension<string>) {
  if (ext.compat && ext.compat[SDKEnvironment.sdkName] != null) {
    return typeof ext.compat[SDKEnvironment.sdkName] === 'string'
      ? satisfies(coerce(SDKEnvironment.version), ext.compat[SDKEnvironment.sdkName] as string)
      : !!ext.compat[SDKEnvironment.sdkName];
  }

  // To gracefully support older extensions, we assume
  // compatibility when the `compat` field is missing.
  return true;
}

/**
 * Initializes SDK extensions, checks for platform/version compatiblity issues,
 * then consolidates any global configurations provided by those extensions.
 */
function prepareExtensions(this: SDKBase, options: TronicSDKAdditionalConfiguration): Record<string, any> {
  const extensions: Extension<string>[] | { [key: string]: Extension<string> } = options.extensions ?? [];
  const extConfig: any = {};
  const incompatibleExtensions: Extension<string>[] = [];

  if (Array.isArray(extensions)) {
    extensions.forEach((ext) => {
      if (checkExtensionCompat(ext)) {
        ext.init(this);
        if (ext.name || ext.name !== Extension.Anonymous) {
          // Only apply extensions with a known, defined `name` parameter.
          (this as any)[ext.name] = ext;
        }
        if (ext instanceof Extension.Internal) {
          if (!isEmpty(ext.config)) extConfig[ext.name] = ext.config;
        }
      } else {
        incompatibleExtensions.push(ext);
      }
    });
  } else {
    Object.keys(extensions).forEach((name) => {
      if (checkExtensionCompat(extensions[name])) {
        extensions[name].init(this);
        const ext = extensions[name];
        (this as any)[name] = ext;
        if (ext instanceof Extension.Internal) {
          if (!isEmpty(ext.config)) extConfig[extensions[name].name] = ext.config;
        }
      } else {
        incompatibleExtensions.push(extensions[name]);
      }
    });
  }

  if (incompatibleExtensions.length) {
    throw createIncompatibleExtensionsError(incompatibleExtensions);
  }

  return extConfig;
}

export type TronicSDKExtensionsOption<TCustomExtName extends string = string> =
  | Extension<string>[]
  | { [P in TCustomExtName]: Extension<string> };

export interface TronicSDKAdditionalConfiguration<
  TCustomExtName extends string = string,
  TExt extends TronicSDKExtensionsOption<TCustomExtName> = any,
> {
  endpoint: string;
  locale?: SupportedLocale;
  extensions?: TExt;
  testMode?: boolean;
  deferPreload?: boolean;
  useStorageCache?: boolean;
  meta?: any; // Generic field for clients to add metadata
}

export class SDKBase {
  private static readonly __overlays__: Map<string, ViewController> = new Map();

  protected readonly endpoint: string;
  protected readonly parameters: string;
  public readonly testMode: boolean;
  public readonly useStorageCache: boolean;

  /**
   * Contains methods for interacting with user data, checking login
   * status, generating cryptographically-secure ID tokens, and more.
   */
  public readonly user: UserModule;

  /**
   * Contains methods previously under the `ConnectExtension`, including
   * login, show wallet UI, request user info, and more.
   */
  public readonly wallet: WalletModule;

  /**
   * Creates an instance of Tronic SDK.
   */
  constructor(public readonly apiKey: string, options: TronicSDKAdditionalConfiguration) {
    if (!apiKey) throw createMissingApiKeyError();
    if (!options.endpoint) throw createMissingEndpointError();

    const { version } = SDKEnvironment;
    this.testMode = !!options.testMode;
    this.useStorageCache = !!options.useStorageCache;
    this.endpoint = createURL(options.endpoint).href;

    // Prepare built-in modules
    this.user = new UserModule(this);
    this.wallet = new WalletModule(this);

    // Prepare extensions
    const extConfig: any = prepareExtensions.call(this, options);

    // Encode parameters as base64-JSON
    this.parameters = encodeJSON<QueryParameters>({
      API_KEY: this.apiKey,
      DOMAIN_ORIGIN: window.location ? window.location.origin : '',
      host: createURL(this.endpoint).host,
      sdk: sdkNameToEnvName[SDKEnvironment.sdkName],
      version,
      ext: isEmpty(extConfig) ? undefined : extConfig,
      locale: options.locale || 'en_US',
      meta: options.meta,
    });

    if (!options.deferPreload) this.preload();
  }

  /**
   * Represents the view controller associated with this `TronicSDK` instance.
   */
  protected get overlay(): ViewController {
    if (!SDKBase.__overlays__.has(this.parameters)) {
      const controller = new SDKEnvironment.ViewController(this.endpoint, this.parameters);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - We don't want to expose this method to the user, but we
      // need to invoke in here so that the `ViewController` is ready for use.
      controller.init();

      SDKBase.__overlays__.set(this.parameters, controller);
    }

    return SDKBase.__overlays__.get(this.parameters)!;
  }

  /**
   * Preloads the Tronic view, allowing for faster initial requests in browser
   * environments. Awaiting the returned promise will signal when the Tronic view
   * has completed loading and is ready for requests.
   */
  public async preload() {
    await this.overlay.checkIsReadyForRequest;
  }
}
